'use client';

import { cn } from '@core/utils';
import Link from 'next/link';
import { useEffect, useState } from 'react';

type TabType = 'benefit' | 'stamp' | 'my-stamp';

type TabConfig = {
  id: TabType;
  sectionId: string;
  label: string;
  subLabel?: string;
};

const TAB_HEIGHT = 68;
const VIEWPORT_THRESHOLD_PERCENTAGE = 50;

const TABS: TabConfig[] = [
  {
    id: 'benefit',
    sectionId: 'benefit',
    label: '웰로 최대 혜택',
    subLabel: '100% 즉시 지급',
  },
  {
    id: 'stamp',
    sectionId: 'challenge',
    label: '스탬프 이벤트',
    subLabel: '쌓일수록 BIG 경품',
  },
  {
    id: 'my-stamp',
    sectionId: 'my-stamp',
    label: '나의 스탬프 현황',
  },
];

const useActiveTab = () => {
  const [activeTab, setActiveTab] = useState<TabType>('benefit');

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    const initialTab = TABS.find((tab) => tab.sectionId === hash);
    if (initialTab) {
      setActiveTab(initialTab.id);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const tab = TABS.find((t) => t.sectionId === entry.target.id);
            if (tab) setActiveTab(tab.id);
          }
        });
      },
      {
        rootMargin: `-${TAB_HEIGHT}px 0px -${VIEWPORT_THRESHOLD_PERCENTAGE}% 0px`,
        threshold: 0,
      },
    );

    TABS.forEach(({ sectionId }) => {
      const element = document.getElementById(sectionId);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return { activeTab, setActiveTab };
};

const TabItem = ({
  tab,
  isActive,
  onClick,
}: {
  tab: TabConfig;
  isActive: boolean;
  onClick: () => void;
}) => {
  const hasSubLabel = !!tab.subLabel;

  return (
    <Link
      href={`#${tab.sectionId}`}
      onClick={onClick}
      role="tab"
      aria-selected={isActive}
      aria-controls={tab.sectionId}
      tabIndex={isActive ? 0 : -1}
      className={cn(
        'flex h-68 flex-1 items-center justify-center border-b-[3px] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-O-500 focus-visible:ring-offset-2',
        hasSubLabel && 'flex-col',
        isActive ? 'border-Gray-black' : 'border-Gray-white',
      )}
    >
      {tab.subLabel && (
        <span
          className={cn(
            'font-800 tracking-tight typo-caption1',
            isActive ? 'text-O-500' : 'text-Gray-500',
          )}
          aria-hidden="true"
        >
          {tab.subLabel}
        </span>
      )}
      <span
        className={cn(
          '-mt-2 font-800 typo-body3',
          isActive ? 'text-Gray-black' : 'text-Gray-500',
        )}
      >
        {tab.label}
      </span>
    </Link>
  );
};

export const Tab = () => {
  const { activeTab, setActiveTab } = useActiveTab();

  return (
    <div
      className="sticky top-0 z-[2] flex w-full bg-Gray-white"
      role="tablist"
      aria-label="스탬프 이벤트 네비게이션"
    >
      {TABS.map((tab) => (
        <TabItem
          key={tab.id}
          tab={tab}
          isActive={activeTab === tab.id}
          onClick={() => setActiveTab(tab.id)}
        />
      ))}
    </div>
  );
};
